import { useEffect, useState } from "react";
import api from "@/lib/api";
import TipButton from "@/components/TipButton";
import ThankButton from "@/components/ThankButton";
import TierPortal from "@/components/TierPortal";
import AuthorMenu from "@/components/AuthorMenu";

/**
 * "Added by <@username>" attribution shown on unclaimed team/trainer/event detail pages.
 * Fetches the creator's public card (username, avatar, tip links) and shows tip/thank icons.
 * Renders nothing if there is no creator or the card can't be loaded.
 *
 *  - preferUsername: show @username instead of real name (default true — we never expose real names here).
 *  - block: render as a full-width labeled card matching the Event page "Posted by" box.
 */
export default function CreatorTag({ userId, label = "Posted by", preferUsername = true, block = false }) {
    const [card, setCard] = useState(null);
    useEffect(() => {
        let active = true;
        if (!userId) { setCard(null); return undefined; }
        api.get(`/users/${userId}/card`)
            .then((r) => { if (active) setCard(r.data); })
            .catch(() => { if (active) setCard(null); });
        return () => { active = false; };
    }, [userId]);
    if (!card) return null;
    const initials = (card.username || card.name || "?").slice(0, 1).toUpperCase();
    const displayName = preferUsername ? `${card.username || card.name}` : (card.name || card.username);

    if (block) {
        return (
            <div className="border border-[#2a2a2a] bg-[#141414] p-4 w-full" data-testid="creator-tag">
                <div className="flex flex-col items-start gap-2 min-w-0">
                    <span className="text-[9px] uppercase tracking-[0.1em] text-[#a0a0a0] shrink-0">{label}</span>
                    <div className="flex items-center gap-2 min-w-0 w-full">
                        <div className="shrink-0">
                            <TierPortal count={card.thanks_count} size={18} ring={1.5} gap={1} testid="creator-portal">
                                {card.photo ? (
                                    <img src={card.photo} alt={card.username || card.name} className="h-full w-full object-cover" />
                                ) : (
                                    <span className="h-full w-full flex items-center justify-center bg-[#1a1a1a] text-[10px] text-[#ff4500]">{initials}</span>
                                )}
                            </TierPortal>
                        </div>
                        <span className="min-w-0 truncate text-sm text-white" data-testid="creator-name">{displayName}</span>
                        <div className="flex items-center gap-2 shrink-0">
                            <ThankButton toUser={card.id} toName={card.username || card.name} contextType="profile" contextId={card.id} initialCount={card.thanks_count} />
                            <TipButton tipLinks={card.tip_links} name={card.username || card.name} />
                            <AuthorMenu userId={card.id} name={card.username || card.name} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="inline-flex items-center gap-2 border border-[#2a2a2a] bg-[#141414] px-3 py-1.5" data-testid="creator-tag">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">{label}</span>
            <TierPortal count={card.thanks_count} size={24} ring={2} gap={1.5} testid="creator-portal">
                {card.photo ? (
                    <img src={card.photo} alt={card.username || card.name} className="h-full w-full object-cover" />
                ) : (
                    <span className="h-full w-full flex items-center justify-center bg-[#1a1a1a] text-[10px] text-[#ff4500]">{initials}</span>
                )}
            </TierPortal>
            <span className="text-sm text-white" data-testid="creator-name">{displayName}</span>
            <ThankButton toUser={card.id} toName={card.username || card.name} contextType="profile" contextId={card.id} initialCount={card.thanks_count} />
            <TipButton tipLinks={card.tip_links} name={card.username || card.name} />
            <AuthorMenu userId={card.id} name={card.username || card.name} />
        </div>
    );
}
